import { createFrontendModule } from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
  EntityContentLayoutBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import type { Entity } from '@backstage/catalog-model';

export function hasRelation(entity: Entity, type: string, kind: string) {
  return (
    entity.kind.toLowerCase() === 'component' &&
    Boolean(
      entity.relations?.some(
        relation =>
          relation.type === type &&
          relation.targetRef.toLowerCase().startsWith(`${kind}:`),
      ),
    )
  );
}

export const deliveryCatalogPlugin: typeof catalogPlugin =
  catalogPlugin.withOverrides({
    extensions: [
      EntityCardBlueprint.make({
        name: 'depends-on-components',
        params: {
          filter: entity => hasRelation(entity, 'dependsOn', 'component'),
          loader: async () => {
            const { EntityDependsOnComponentsCard } = await import(
              '@backstage/plugin-catalog'
            );
            return <EntityDependsOnComponentsCard />;
          },
        },
      }),
      EntityCardBlueprint.make({
        name: 'depends-on-resources',
        params: {
          filter: entity => hasRelation(entity, 'dependsOn', 'resource'),
          loader: async () => {
            const { EntityDependsOnResourcesCard } = await import(
              '@backstage/plugin-catalog'
            );
            return <EntityDependsOnResourcesCard />;
          },
        },
      }),
      EntityCardBlueprint.make({
        name: 'has-subcomponents',
        params: {
          filter: entity => hasRelation(entity, 'hasPart', 'component'),
          loader: async () => {
            const { EntityHasSubcomponentsCard } = await import(
              '@backstage/plugin-catalog'
            );
            return <EntityHasSubcomponentsCard />;
          },
        },
      }),
    ],
  });

export const catalogDeliveryModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    EntityContentLayoutBlueprint.make({
      name: 'component-overview',
      params: {
        filter: entity => entity.kind.toLowerCase() === 'component',
        loader: async () =>
          (await import('./ComponentOverviewLayout')).ComponentOverviewLayout,
      },
    }),
    EntityContentBlueprint.make({
      name: 'delivery-deployments',
      params: {
        path: '/deployments',
        title: 'Deployments',
        filter: entity =>
          entity.kind.toLowerCase() === 'component' &&
          entity.spec?.type === 'service',
        loader: async () => {
          const { EntityDeliveryPage } = await import('./EntityDeliveryPage');
          return <EntityDeliveryPage />;
        },
      },
    }),
  ],
});
